import { FormEvent, useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import RestrictedRoute from "../../auth/RestrictedRoute";
import { ROLES } from "../../../config/constants";

const DEFAULT_PLACE_ADMIN_PARTNER_INFO = {
  id: "",
  email: "",
  phone: "",
  fullName: "",
  password: "",
  confirmPassword: "",
  placeCommission: 0,
  acceptedTerms: true,
  birthday: new Date(),
  lang: "fr",
};
interface Props {
  onClose: () => void;
  onAccept: () => void;
  id?: string;
  user?: User;
  open: boolean;
}

interface createPlaceAdminPartnerType {
  email: string;
  phone: string;
  fullName: string;
  password: string;
  placeCommission: number;
  acceptedTerms?: boolean;
  birthday?: Date;
  lang?: string;
  id?: string;
}

const validationErrorMessages = {
  INVALID_PASSWORD: "Veuillez fournir un email valide",
  PASSWORD_MISMATCH: "Les mots de passe ne correspondent pas",
  PHONE_INCORRECT: "Numéro de téléphone incorrect",
  PLACE_COMMISSION_INCORRECT: "Commission de l'établissement incorrecte",
};
export default function NewPlaceAdminPartner({
  onClose,
  onAccept,
  open,
  id,
  user,
}: Props) {
  const [placeAdminPartnerInfo, setPlaceAdminPartnerInfo] = useState(
    DEFAULT_PLACE_ADMIN_PARTNER_INFO,
  );
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const handlePlaceAdminPartnerInfo = useCallback(
    (key: string, value: string | number) => {
      setPlaceAdminPartnerInfo((info) => ({ ...info, [key]: value }));
    },
    [],
  );

  const createPlaceAdminPartner = useMutation(
    (userData: createPlaceAdminPartnerType) => {
      return apiService.MakePostRequest(
        "users/place-manager",
        userData,
        token,
        false,
      );
    },
  );

  const updatePlaceAdminPartner = useMutation(
    (userData: createPlaceAdminPartnerType) => {
      return apiService.MakePutRequest(
        `users/place-manager/${id}`,
        userData,
        token,
        false,
      );
    },
  );

  useEffect(() => {
    if (user && id) {
      setPlaceAdminPartnerInfo({
        ...DEFAULT_PLACE_ADMIN_PARTNER_INFO,
        id: user.id,
        email: user.email,
        phone: user.phone,
        fullName: user.fullName || "",
        placeCommission: Number(user.placeCommission) || 0,
      });
    } else if (!id && open) {
      setPlaceAdminPartnerInfo(DEFAULT_PLACE_ADMIN_PARTNER_INFO);
    }
  }, [user, id, open]);

  if (!open) return null;

  const validateInputs = () => {
    // eslint-disable-next-line no-useless-escape
    const emailReg = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    const phoneReg = /(\+212|0)([ \-_/]*)(\d[ \-_/]*){9}/g;
    // const placeCommission = Number(placeAdminPartnerInfo.placeCommission);
    // const placeCommissionReg =
    // isNaN(placeCommission) || placeCommission <= 0 || placeCommission > 100;
    let validationError = false;

    if (!emailReg.test(placeAdminPartnerInfo.email)) {
      setEmailError(validationErrorMessages.INVALID_PASSWORD);
      validationError = true;
    } else {
      setEmailError("");
    }

    if (!id || placeAdminPartnerInfo.password) {
      if (
        placeAdminPartnerInfo.confirmPassword !== placeAdminPartnerInfo.password
      ) {
        validationError = true;
        setPasswordError(validationErrorMessages.PASSWORD_MISMATCH);
      } else {
        setPasswordError("");
      }
    }
    if (!phoneReg.test(placeAdminPartnerInfo.phone)) {
      validationError = true;
      setPhoneError(validationErrorMessages.PHONE_INCORRECT);
    } else {
      setPhoneError("");
    }

    return validationError;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateInputs()) return;

    const isUpdateMode = id && id.trim() !== "";

    if (isUpdateMode) {
      const updateData: createPlaceAdminPartnerType = {
        email: placeAdminPartnerInfo.email,
        phone: placeAdminPartnerInfo.phone,
        fullName: placeAdminPartnerInfo.fullName,
        placeCommission: placeAdminPartnerInfo.placeCommission,
        password: placeAdminPartnerInfo.password || "",
      };

      if (!placeAdminPartnerInfo.password) {
        delete (updateData as any)?.password;
      }

      updatePlaceAdminPartner.mutate(updateData, {
        onError() {
          toastError(general.fr.message.operationFailed);
        },
        onSuccess() {
          toastSuccess(general.fr.message.placeAdminPartnerUpdated);
          setPlaceAdminPartnerInfo(DEFAULT_PLACE_ADMIN_PARTNER_INFO);
          queryClient.invalidateQueries(["getMultiPlaceAdminPartners"]);

          onAccept();
          onClose();
        },
      });
    } else {
      createPlaceAdminPartner.mutate(placeAdminPartnerInfo, {
        onError() {
          toastError(general.fr.message.operationFailed);
        },
        onSuccess() {
          toastSuccess(general.fr.message.placeAdminPartnerCreated);
          setPlaceAdminPartnerInfo(DEFAULT_PLACE_ADMIN_PARTNER_INFO);
          queryClient.invalidateQueries(["getMultiPlaceAdminPartners"]);
          onAccept();
          onClose();
        },
      });
    }
  };

  return (
    <RestrictedRoute requireMultiPlaceAdmin={true} route={ROLES.PLACE_ADMIN}>
      <div className="">
        <section className="p-4">
          <div className="border-bottom border-success">
            <h4>Ajouter Partenaire</h4>
          </div>
          <form className="row form mt-2" onSubmit={handleSubmit}>
            <div className="col-md-6 mt-3">
              <label htmlFor="fullName" className="d-block">
                Nom de partenaire
              </label>
              <input
                type="text"
                className="custom-input"
                placeholder="Nom de partenaire"
                id="fullName"
                name="fullName"
                onChange={(e) =>
                  handlePlaceAdminPartnerInfo("fullName", e.target.value)
                }
                required
                value={placeAdminPartnerInfo.fullName}
              />
            </div>

            <div className="col-md-6 mt-3">
              <label htmlFor="email" className="d-block">
                Email du partenaire
              </label>
              <input
                type="email"
                className="custom-input"
                placeholder="Email du partenaire"
                id="email"
                name="email"
                onChange={(e) =>
                  handlePlaceAdminPartnerInfo("email", e.target.value)
                }
                required
                value={placeAdminPartnerInfo.email}
              />
              {emailError && (
                <p className="text-danger text-sm">{emailError}</p>
              )}
            </div>

            <div className="col-md-6 mt-3">
              <label htmlFor="phone" className="d-block">
                Téléphone du partenaire
              </label>
              <input
                type="text"
                className="custom-input"
                placeholder="Téléphone du partenaire"
                id="phone"
                name="phone"
                onChange={(e) =>
                  handlePlaceAdminPartnerInfo("phone", e.target.value)
                }
                required
                value={placeAdminPartnerInfo.phone}
              />{" "}
              {phoneError && (
                <p className="text-danger text-sm">{phoneError}</p>
              )}
            </div>

            <div className="col-md-6 mt-3"></div>

            <div className="col-md-6 mt-3">
              <label htmlFor="Password" className="d-block">
                Mot de passe
              </label>
              <input
                type="password"
                className="custom-input"
                placeholder="Password"
                id="password"
                name="password"
                onChange={(e) =>
                  handlePlaceAdminPartnerInfo("password", e.target.value)
                }
                required={!id}
                value={placeAdminPartnerInfo.password}
              />{" "}
            </div>
            <div className="col-md-6 mt-3">
              <label htmlFor="cPassword" className="d-block">
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                className="custom-input"
                placeholder="confirm password"
                id="cPassword"
                name="cPassword"
                onChange={(e) =>
                  handlePlaceAdminPartnerInfo("confirmPassword", e.target.value)
                }
                value={placeAdminPartnerInfo.confirmPassword}
                required={!id}
              />{" "}
              {passwordError && (
                <p className="text-danger text-sm">{passwordError}</p>
              )}
            </div>

            <section className="d-flex justify-content-center buttons gap-4 mt-5">
              <button
                className="btn btn-danger red-shadow w-20 px-4 py-2 shadow-primary text-white"
                type="button"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary text-white shadow-secondary w-20 px-4"
                type="submit"
              >
                Enregistrer
              </button>
            </section>
          </form>
        </section>
      </div>
    </RestrictedRoute>
  );
}
